/**
 * Access Platform observability API (design 19 §7, WP4).
 *
 * Two kinds of call live here and they are deliberately in one module, because
 * every page in this area mixes them:
 *
 *  1. **Records** — `/api/v2/access/{sessions,activity,audit/*}`: MySQL rows and
 *     the tenant-guarded Loki wrapper. Identities live here.
 *  2. **Series** — `/api/v1/observability/metrics/*` and `/logs/query_range`:
 *     the tenant-guarded PromQL/LogQL proxy. No identities, by design.
 *
 * # Two rules this module exists to keep
 *
 * **The client never names a tenant in a query.** Every PromQL string below is
 * written WITHOUT a `tenant=` matcher; the API injects the caller's tenant plus
 * the node allow-list server-side (`observability.ts:applyTenantGuard`). Typing
 * a tenant label here would be a filter, not a guard — anyone can edit a
 * request — and it would fight the injected one.
 *
 * **Fleet columns are one query per COLUMN, not one per row.** A table of 40
 * gateways asks eight `by (node)` questions, not 320 single-node ones. Hence
 * `instantByNode`, which returns a node→value map a table can index.
 */

import api, { type RequestOptions } from './api';
import { observabilityService, type MetricResult } from './observability.service';
import { useAuthStore } from '../stores/authStore';
import { useTenantStore } from '../stores/tenantStore';
import { USER_TYPES } from '../utils/constants';

const BASE = '/api/v2/access';

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  message?: string;
  timestamp: string;
}

function unwrap<T>(response: unknown): T {
  return (response as ApiEnvelope<T>).data;
}

/**
 * Explicit tenant scoping for SuperAdmins (same contract as access.service).
 * Tenant admins are pinned server-side and send nothing.
 */
function tenantParams(): Record<string, string> {
  const { user } = useAuthStore.getState();
  if (user?.type !== USER_TYPES.SUPER_ADMIN) return {};
  const tenantId = useTenantStore.getState().getTenantId();
  return tenantId ? { tenantId } : {};
}

// ---------------------------------------------------------------------------
// Records
// ---------------------------------------------------------------------------

export interface AccessSessionRow {
  id: string;
  tenantId: string;
  gatewayId: string | null;
  node: string;
  sessionId: string;
  kind: 'client' | 'connector' | string;
  user: string | null;
  /**
   * The verified `sub` and `email` behind `user` (VSNP-1339). They are what a
   * user filter resolves the person by — never the device's owner, who on a
   * shared device is somebody else. Null on a device-only session and on rows
   * recorded before the gateway reported them.
   */
  userSub: string | null;
  userEmail: string | null;
  deviceCn: string | null;
  deviceName: string | null;
  serial: string | null;
  deviceId: string | null;
  srcIp: string | null;
  openedAt: string | null;
  closedAt: string | null;
  durationSeconds: number | null;
  closeReason: string | null;
  rejectReason: string | null;
  detail: string | null;
  postureSource: string | null;
  /**
   * False on a device-only session (skip-login): the gateway falls back to the
   * device CN as the policy subject, so `user` carries a CN, not a person.
   * Every UI that renders a "User" must branch on this — see `displayUser`.
   */
  idpVerified: boolean;
  /** Token binding outcome at admission (WP8B); null when no token was presented. */
  tokenBinding: string | null;
  flows: number;
  bytesC2s: number;
  bytesS2c: number;
  /**
   * The top (app, host) pairs this session touched, as the gateway reported
   * them on close or on a five-minute progress record (design §9b).
   *
   * `null` means the node never reported one — an older gateway build, or a
   * session that moved nothing. An empty array would claim it reported none,
   * so the two are not collapsed.
   */
  apps: SessionAppRollup[] | null;
  reportedAt: string | null;
  receivedAt: string;
}

/** One (app, host) pair's share of a session's traffic. */
export interface SessionAppRollup {
  app?: string;
  host?: string;
  flows: number;
  bytesC2s: number;
  bytesS2c: number;
}

/**
 * The Dashboard's range summary (design §9b) — six numbers computed in SQL over
 * every session that OVERLAPPED the range, replacing a count of a 500-row page.
 */
export interface SessionsSummary {
  from: string;
  to: string;
  avgConcurrent: number;
  peakConcurrent: number;
  distinctUsers: number;
  distinctDeviceOnly: number;
  sessions: number;
  bytesC2s: number;
  bytesS2c: number;
  bytesTotal: number;
}

export interface ListSessionsParams {
  user?: string;
  deviceCn?: string;
  gatewayId?: string;
  kind?: 'client' | 'connector';
  from?: string;
  to?: string;
  openOnly?: boolean;
  limit?: number;
  offset?: number;
}

export interface AccessActivityRow {
  key: string;
  user?: string;
  deviceCn?: string;
  app?: string;
  /** Set when grouping by host (design §9b): the website, not the app. */
  host?: string;
  gatewayId?: string;
  sessions: number;
  requests: number;
  allows: number;
  denies: number;
  bytesC2s: number;
  bytesS2c: number;
  bytesTotal: number;
  firstSeen: string | null;
  lastSeen: string | null;
}

export interface ActivityParams {
  from?: string;
  to?: string;
  user?: string;
  deviceCn?: string;
  app?: string;
  host?: string;
  gatewayId?: string;
  groupBy?: 'user' | 'device' | 'app' | 'host' | 'gateway';
  rankBy?: 'requests' | 'bytes' | 'denies' | 'sessions';
  limit?: number;
}

export interface AuditEvent {
  ts: string;
  node: string;
  nodeKind?: string;
  tenant?: string;
  event?: string;
  class?: string;
  user?: string;
  /** The verified sub / email — carried on session-class events only (VSNP-1339). */
  userSub?: string;
  userEmail?: string;
  device?: string;
  serial?: string;
  session?: string;
  src?: string;
  app?: string;
  dest?: string;
  layer?: string;
  rule?: string;
  bundle?: string;
  reason?: string;
  detail?: string;
  seq?: number;
  /** The hostname the flow reached (design §9b); absent when none was seen. */
  host?: string;
  raw: Record<string, unknown>;
}

export interface AuditEventsParams {
  user?: string;
  deviceCn?: string;
  app?: string;
  event?: string;
  action?: 'allow' | 'deny' | 'error';
  reason?: string;
  eventClass?: 'session' | 'flow' | 'posture' | 'policy' | 'connector' | 'node';
  sessionId?: string;
  gatewayId?: string;
  from?: string;
  to?: string;
  limit?: number;
  search?: string;
}

export interface AuditEventsResult {
  from: string;
  to: string;
  count: number;
  query: string;
  events: AuditEvent[];
}

export interface AuditExportParams extends Omit<AuditEventsParams, 'from' | 'to'> {
  format: 'csv' | 'jsonl';
  from: string;
  to: string;
  /**
   * Several event names in one export (doc 22 §10.1).
   *
   * The Security table is the union of six audit queries; an export that sent
   * only `event` gave an auditor a subset of what they were looking at without
   * saying so. The server runs one indexed query per name and merges them.
   */
  events?: string[];
}

// ---------------------------------------------------------------------------
// Series
// ---------------------------------------------------------------------------

/** One point per node for a `by (node)` instant query. */
export type ByNode = Record<string, number>;

function firstValue(series: MetricResult): number | null {
  if (series.value) return Number(series.value[1]);
  if (series.values?.length) return Number(series.values[series.values.length - 1][1]);
  return null;
}

/** One metric series' samples, in time order. */
export interface StateSeries {
  name: string;
  points: Array<{ t: number; v: number }>;
}

export interface ChartPoint {
  timestamp: number;
  [series: string]: number;
}

/**
 * Fold a Prometheus matrix into recharts rows, one key per series.
 *
 * `label` names the series from its own labels, so a caller decides whether a
 * chart is "by app", "by reason" or "by node" without a second transform.
 */
export function toChartPoints(
  results: MetricResult[],
  label: (metric: Record<string, string>) => string,
): ChartPoint[] {
  const byTs = new Map<number, ChartPoint>();
  for (const series of results ?? []) {
    const key = label(series.metric) || 'value';
    for (const [ts, val] of series.values ?? []) {
      const point = byTs.get(ts) ?? { timestamp: ts };
      point[key] = Number(val);
      byTs.set(ts, point);
    }
  }
  return Array.from(byTs.values()).sort((a, b) => a.timestamp - b.timestamp);
}

/**
 * A metrics query that the store could not answer (VSNP-1265).
 *
 * The four series helpers below used to turn a non-`success` response into an
 * empty map and return it, so a VictoriaMetrics outage and "this gateway has
 * reported nothing yet" arrived at the caller as the same value — an empty
 * object. The fleet table then rendered nine columns of `—` with no
 * explanation, which reads as "your gateways are idle" rather than "the
 * monitoring store is down".
 *
 * Throwing is what makes the two distinguishable. A caller that does not care
 * still writes `catch { setX(empty) }` and behaves exactly as before; a caller
 * that does can say so.
 */
export class MetricsQueryError extends Error {
  constructor(message = 'The metrics store did not answer') {
    super(message);
    this.name = 'MetricsQueryError';
  }
}

export const accessObservabilityService = {
  // ---------- records ----------

  listSessions: async (params: ListSessionsParams = {}, options?: RequestOptions) => {
    const response = await api.get(`${BASE}/sessions`, { params: { ...params }, ...options });
    return unwrap<{ sessions: AccessSessionRow[]; total: number; limit: number; offset: number }>(response);
  },

  getSessionsSummary: async (
    params: { from?: string; to?: string } = {},
    options?: RequestOptions,
  ) => {
    const response = await api.get(`${BASE}/sessions/summary`, { params: { ...params }, ...options });
    return unwrap<SessionsSummary>(response);
  },

  getActivity: async (params: ActivityParams = {}, options?: RequestOptions) => {
    const response = await api.get(`${BASE}/activity`, { params: { ...params }, ...options });
    return unwrap<{
      groupBy: string;
      rankBy: string;
      from: string | null;
      to: string | null;
      rows: AccessActivityRow[];
    }>(response);
  },

  listAuditEvents: async (params: AuditEventsParams = {}, options?: RequestOptions) => {
    const response = await api.get(`${BASE}/audit/events`, { params: { ...params }, ...options });
    return unwrap<AuditEventsResult>(response);
  },

  /**
   * Export a filtered range (design §7.6).
   *
   * Returns the body as text rather than saving it: the caller decides whether
   * to offer a download, and the API has already written the `activity_log` row
   * that records the extraction happened.
   */
  exportAudit: async (params: AuditExportParams): Promise<string> => {
    const response = await api.post(`${BASE}/audit/export`, params, {
      params: tenantParams(),
      responseType: 'text',
      // The export is a file body, not the {success,data} envelope, so the
      // usual unwrap would corrupt it.
      transformResponse: [(d: string) => d],
    });
    return response as unknown as string;
  },

  // ---------- series ----------

  /**
   * Run one `by (node)` instant query and return a node→value map.
   *
   * The unit of work is a COLUMN. Calling this once per table row is the thing
   * this helper exists to prevent.
   */
  instantByNode: async (query: string, options?: RequestOptions): Promise<ByNode> => {
    const res = await observabilityService.query(query, undefined, { showLoading: false, ...options });
    if (res?.status !== 'success') throw new MetricsQueryError();
    const out: ByNode = {};
    for (const series of res.data.result ?? []) {
      const node = series.metric.node;
      if (!node) continue;
      const v = firstValue(series);
      if (v !== null && Number.isFinite(v)) out[node] = v;
    }
    return out;
  },

  /**
   * `by (node, <label>)` instant query → node → label value → value. For a
   * per-node series that is itself per tenant (agw_policy_bundle_epoch), where
   * folding the tenants together would compare one tenant's bundle with
   * another's compiled epoch (VSNP-1434).
   */
  instantByNodeAnd: async (
    query: string,
    labelKey: string,
    options?: RequestOptions,
  ): Promise<Record<string, Record<string, number>>> => {
    const res = await observabilityService.query(query, undefined, { showLoading: false, ...options });
    if (res?.status !== 'success') throw new MetricsQueryError();
    const out: Record<string, Record<string, number>> = {};
    for (const series of res.data.result ?? []) {
      const node = series.metric.node;
      if (!node) continue;
      const v = firstValue(series);
      if (v !== null && Number.isFinite(v)) (out[node] ??= {})[series.metric[labelKey] ?? ''] = v;
    }
    return out;
  },

  /** Instant query keyed by an arbitrary label (mount, app, reason, tenant). */
  instantBy: async (
    query: string,
    labelKey: string,
    options?: RequestOptions,
  ): Promise<Record<string, number>> => {
    const res = await observabilityService.query(query, undefined, { showLoading: false, ...options });
    if (res?.status !== 'success') throw new MetricsQueryError();
    const out: Record<string, number> = {};
    for (const series of res.data.result ?? []) {
      const key = series.metric[labelKey] ?? '';
      const v = firstValue(series);
      if (v !== null && Number.isFinite(v)) out[key] = (out[key] ?? 0) + v;
    }
    return out;
  },

  /** Instant query reduced to a single scalar (already aggregated server-side). */
  instantScalar: async (query: string, options?: RequestOptions): Promise<number | null> => {
    const res = await observabilityService.query(query, undefined, { showLoading: false, ...options });
    if (res?.status !== 'success') throw new MetricsQueryError();
    // An empty result IS an answer — "nothing matched" — and stays null.
    if (!res.data.result?.length) return null;
    const v = firstValue(res.data.result[0]);
    return v !== null && Number.isFinite(v) ? v : null;
  },

  /** Range query folded into recharts rows. */
  series: async (
    query: string,
    start: number,
    end: number,
    step: number,
    label: (metric: Record<string, string>) => string,
    options?: RequestOptions,
  ): Promise<ChartPoint[]> => {
    const res = await observabilityService.queryRange(query, start, end, step, {
      showLoading: false,
      ...options,
    });
    if (res?.status !== 'success') throw new MetricsQueryError();
    return toChartPoints(res.data.result ?? [], label);
  },

  /**
   * Range query kept as PER-SERIES samples rather than folded into chart rows.
   *
   * `series` above pivots the matrix into one row per timestamp, which is what
   * a line chart wants and exactly what a STATE timeline cannot use
   * (VSNP-1261): a timeline asks "what was this one component doing between
   * 10:03 and 10:07", and that question is per series, not per instant.
   *
   * It also keeps the gaps. `toChartPoints` leaves a missing sample absent and
   * recharts simply joins across it, so a component that stopped reporting
   * looks identical to one that stayed up. Here a gap is a value the caller
   * can render as its own state.
   */
  stateSeries: async (
    query: string,
    start: number,
    end: number,
    step: number,
    label: (metric: Record<string, string>) => string,
    options?: RequestOptions,
  ): Promise<StateSeries[]> => {
    const res = await observabilityService.queryRange(query, start, end, step, {
      showLoading: false,
      ...options,
    });
    if (res?.status !== 'success') throw new MetricsQueryError();
    return (res.data.result ?? []).map((series) => ({
      name: label(series.metric) || 'value',
      points: (series.values ?? []).map(([t, v]) => ({ t, v: Number(v) })),
    }));
  },
};

// ---------------------------------------------------------------------------
// Identity display rule (design decision 2026-09-16; brief §3 WP4)
// ---------------------------------------------------------------------------

/**
 * How a session's subject may be shown.
 *
 * On a device-only session the gateway sets `user` to the certificate CN,
 * because that is what the policy engine matched. The DATA is right; showing it
 * in a "User" column is not — it reads as a person who logged in. So:
 * `idpVerified === false` → "device-only", and the CN appears in the Device
 * column only.
 */
export function displayUser(row: { user: string | null; idpVerified: boolean }): {
  label: string;
  deviceOnly: boolean;
  title: string;
} {
  if (row.idpVerified && row.user) {
    return { label: row.user, deviceOnly: false, title: row.user };
  }
  return {
    label: 'device-only',
    deviceOnly: true,
    title: 'no IdP identity — matched by device certificate',
  };
}

/** Ranking label for a "by user" roll-up row that is really a device CN. */
export function rankingUserLabel(key: string, idpVerified: boolean): string {
  return idpVerified ? key : `device-only (${key})`;
}

export default accessObservabilityService;
